import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import * as IbanKit from 'ibankit'
import { Maybe } from 'graphql/jsutils/Maybe'
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup/dist/yup'
import { countries } from '@klarpay/enums'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  TextField,
  useTheme,
  Radio,
  RadioGroup,
  InputAdornment,
  styled,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { debounce, isEmpty, omit, some } from 'lodash'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import Checked from '../../assets/images/icons/icon-checkmark.svg?react'
import {
  ContractPaymentRecipient,
  PaymentRecipientDocument,
  PaymentRecipientsDocument,
  SwiftTransferRecipientType,
  useAddContractPaymentRecipientMutation,
  useGetBankInfoLazyQuery,
  useUpdateContractPaymentRecipientMutation,
} from '../../graphql'
import { SpecificRequiredFields } from '../../pages/NewPaymentPage/Forms/SpecificRequiredFields'
import { PATH_PARAMS } from '../../routes/paths'
import { RecipientSchema } from '../../schemes/RecipientSchema'
import { recipientTypeList } from '../../stubs'
import { AccountIdTypeEnum } from '../../types'
import { LoadingButton } from './Buttons'
import { CustomPrompt } from './CustomPrompt'
import { FormControlledTextField } from './Fields'
import { AddressAutocompleteSelect, FormAutocompleteSelect, PlaceResult } from './Selectors'
import { CountryAutocompleteSelect } from './Selectors/CountryAutocompleteSelect'
import config from '../../config'
import ControlledIconTooltip from './Tooltips/ControlledIconTooltip'

const StyledFormControl = styled(FormControl)({
  width: '100%',
  '& .MuiInputBase-input': {
    paddingTop: 0,
  },
  '& .MuiFormControl-root': {
    margin: 0,
  },
})

export const BeneficiaryModal: FC<{
  handleClose: (createdBeneficiary?: ContractPaymentRecipient) => void
  refetchData?: () => void
  recipient?: ContractPaymentRecipient | undefined | null
  destinationBankCountry?: string
}> = ({ handleClose, refetchData, recipient, destinationBankCountry }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('xs'))

  const [showWarning, setShowWarning] = useState(false)

  const { [PATH_PARAMS.applicationId]: applicationId } = useParams() as Record<string, string>

  const [getBankInfo, { data }] = useGetBankInfoLazyQuery()

  const debouncedGetBankInfo = useCallback(
    debounce((ibanValue = '') => {
      const normalizedIban = IbanKit.IBAN.electronicFormat(ibanValue.trim())
      if (!IbanKit.IBAN.isValid(normalizedIban)) return
      const variables = {
        iban: normalizedIban,
      }
      void getBankInfo({
        variables,
      })
    }, 200),
    [getBankInfo],
  )

  const [
    addContractPaymentRecipientMutation,
    { loading: addRecipientLoading },
  ] = useAddContractPaymentRecipientMutation()
  const [
    updateContractPaymentRecipientMutation,
    { loading: editRecipientLoading },
  ] = useUpdateContractPaymentRecipientMutation()

  const personDefaultName = recipient?.firstName && `${recipient?.firstName} ${recipient?.lastName}`
  const recipientDefaultName = recipient?.companyName || personDefaultName

  const destinationSupportsIban = useMemo(() => {
    return destinationBankCountry
      ? config.ibanSupportedCountries.includes(destinationBankCountry.toUpperCase())
      : true // Setting to true For Fallback when destCountry no passed
  }, [destinationBankCountry])

  const defaultValues = {
    recipientType: recipient?.type || '',
    accountIdType:
      recipient?.accountNo || !destinationSupportsIban
        ? AccountIdTypeEnum.accountNumber
        : AccountIdTypeEnum.iban,
    accountNumber: recipient?.accountNo || '',
    iban: recipient?.iban || '',
    swift: recipient?.bankBic || '',
    bankName: recipient?.bankName || '',
    name: recipientDefaultName || '',
    country: recipient?.address?.country || null,
    city: recipient?.address?.city || '',
    postalCode: recipient?.address?.zip || '',
    streetAddress: recipient?.address?.line1 || '',
    alias: recipient?.alias || '',
    commentTemplate: recipient?.commentTemplate,
    additionalDetailsOptional: recipient?.address?.additionalDetails || '',
    creditorContractPhone: recipient?.phone || '',
    creditorTaxId: recipient?.taxId || '',
    memberId: recipient?.bankClearingMemberId || '',
    agentBranchId: recipient?.bankBranchId || '',
    agentBranchName: recipient?.bankBranchName || '',
    destinationBankCountry: destinationBankCountry || '',
    accountNoType: recipient?.accountNoType || '',
  }

  const methods = useForm({
    mode: 'onChange',
    reValidateMode: 'onBlur',
    defaultValues,
    resolver: yupResolver(RecipientSchema),
  })

  const {
    handleSubmit,
    errors,
    control,
    watch,
    setValue,
    trigger,
    formState,
    getValues,
    register,
  } = methods

  const accountIdType = watch('accountIdType')
  const iban = watch('iban')
  const country = watch('country')
  const swiftBic = watch('swift')
  const phone = watch('creditorContractPhone')

  useEffect(() => {
    if (!destinationSupportsIban) {
      setValue('accountIdType', AccountIdTypeEnum.accountNumber)
      trigger('accountIdType')
    }
  }, [destinationSupportsIban, setValue])

  useEffect(() => {
    register('destinationBankCountry')
    register('accountIdType')
  }, [register])

  const accDetailsCheck = useMemo(() => {
    const isAccDisabled = !!recipient?.iban
    const isIbanDisabled = !!recipient?.accountNo
    return { isAccDisabled, isIbanDisabled }
  }, [recipient])

  const countryForSpecificRequiredFields =
    String(swiftBic)?.substring(4, 6)?.toLowerCase() || country

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const onSubmit = async (formData) => {
    try {
      const baseVariables = {
        city: formData.city,
        zip: formData.postalCode,
        country: formData.country,
        line1: formData.streetAddress,
        bankName: formData.bankName,
        companyName: formData.name,
        firstName: formData.name,
        iban: formData.iban?.trim(),
        accountNo: formData.accountNumber,
        bankBic: formData.swift || null,
        type: formData.recipientType as SwiftTransferRecipientType,
        additionalDetailsOptional: formData.additionalDetailsOptional,
        ...(formData?.alias ? { alias: formData.alias } : {}),
        ...(formData?.commentTemplate ? { commentTemplate: formData.commentTemplate } : {}),
        ...(formData?.creditorContractPhone ? { phone: formData.creditorContractPhone } : {}),
        ...(formData?.creditorTaxId ? { taxId: formData.creditorTaxId } : {}),
        ...(formData?.memberId ? { bankClearingMemberId: formData.memberId } : {}),
        ...(formData?.agentBranchId ? { bankBranchId: formData.agentBranchId } : {}),
        ...(formData?.agentBranchName ? { bankBranchName: formData.agentBranchName } : {}),
        ...(formData?.accountNoType ? { accountNoType: formData.accountNoType } : {}),
        ...(formData?.destinationBankCountry
          ? { bankCountry: formData.destinationBankCountry }
          : {}),
      }
      if (recipient) {
        await updateContractPaymentRecipientMutation({
          variables: { ...baseVariables, id: recipient.id },
          refetchQueries: [{ query: PaymentRecipientDocument, variables: { id: recipient.id } }],
        })
        handleClose()
      } else {
        const { data } = await addContractPaymentRecipientMutation({
          variables: { ...baseVariables, contractId: applicationId },
          refetchQueries: [
            {
              query: PaymentRecipientsDocument,
              variables: { contractId: applicationId, search: null },
            },
          ],
        })

        if (refetchData) refetchData()
        handleClose(data?.addContractPaymentRecipient as ContractPaymentRecipient)
      }
    } catch (e) {
      toast.error((e as Error).message)
    }
  }

  const handleAccountIdTypeChange = () => {
    accountIdType === AccountIdTypeEnum.iban
      ? setValue('iban', defaultValues.iban)
      : setValue('accountNumber', defaultValues.accountNumber)
  }

  const clearFields = () => {
    setValue('postalCode', '')
    setValue('city', '')
    setValue('streetAddress', '')
  }

  const autocompleteFields = (place: PlaceResult): void => {
    const { country, postalCode, city } = place
    setValue('country', country?.toLowerCase())
    setValue('postalCode', postalCode)
    city && setValue('city', city)
    void trigger(['city', 'postalCode', 'country'])
  }

  const handleModalClose = () => {
    const allValues = getValues()
    const checkedValues = omit(allValues, 'accountIdType')
    const isFormChanged = recipient
      ? formState.isDirty
      : some(checkedValues, (val: Maybe<string> | undefined) => !!val?.trim())

    if (isFormChanged) {
      setShowWarning(true)
      return
    }

    handleClose()
  }

  const onPaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault()
    const pasted = event.clipboardData.getData('Text')
    const cleaned = pasted.replace(/\s+/g, '')
    const target = event.target as HTMLInputElement
    const fieldName = target.name

    setValue(fieldName as keyof typeof defaultValues, cleaned, {
      shouldDirty: true,
      shouldValidate: true,
    })
    if (target.name !== 'iban') return
    debouncedGetBankInfo(cleaned)
  }

  useEffect(() => {
    if (iban && !isEmpty(data?.bankInfo)) {
      const { bic: swift, bankName } = data?.bankInfo || {}
      if (swift) {
        setValue('swift', swift)
        void trigger('swift')
      }
      if (bankName) {
        setValue('bankName', bankName)
        void trigger('bankName')
      }
    } else {
      if (!recipient) {
        setValue('swift', '')
        setValue('bankName', '')
      }
    }
  }, [data, iban])

  useEffect(() => {
    if (defaultValues.creditorContractPhone && !phone) {
      setValue('creditorContractPhone', defaultValues.creditorContractPhone)
    }
  }, [phone, defaultValues])

  return (
    <>
      <Dialog open fullWidth>
        <Box padding={isSmallScreen ? 0 : 3}>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <DialogTitle
                component="div"
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="h5">
                  {recipient
                    ? t('editBeneficiary', 'Edit beneficiary')
                    : t('addBeneficiary', 'Add beneficiary')}
                </Typography>
                <IconButton
                  aria-label="close"
                  onClick={handleModalClose}
                  disableRipple
                  disableFocusRipple
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </DialogTitle>

              <DialogContent
                sx={() => ({
                  display: 'flex',
                  flexDirection: 'column',
                  gap: theme.spacing(5),
                  [theme.breakpoints.down('xs')]: {
                    gap: theme.spacing(3),
                  },
                })}
              >
                <FormControl fullWidth>
                  <FormAutocompleteSelect
                    label={t('beneficiaryType', 'Beneficiary type')}
                    name="recipientType"
                    data={recipientTypeList}
                    readOnly={!!recipient}
                  />
                </FormControl>

                <Box hidden={!!recipient}>
                  <FormControl component="fieldset" error={!!errors.accountIdType} fullWidth>
                    <FormLabel
                      sx={{
                        paddingLeft: 1.5,
                        color: '#999',
                        '&.Mui-focused': {
                          color: '#999',
                        },
                      }}
                    >
                      {t('accountIdType', 'Account ID Type')}
                    </FormLabel>

                    <Box
                      sx={(theme) => ({
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        padding: theme.spacing(1, 1.5),
                      })}
                    >
                      <Controller
                        control={control}
                        name="accountIdType"
                        render={({ value, onChange }) => (
                          <RadioGroup
                            row
                            value={value}
                            onChange={(e) => {
                              handleAccountIdTypeChange()
                              onChange(e.target.value)
                            }}
                            aria-label="payment type"
                          >
                            <Tooltip
                              title={t(
                                'destinationIbanNotSupported',
                                'Destination Country does not support IBAN',
                              )}
                              placement="top"
                              arrow
                              disableFocusListener={destinationSupportsIban}
                              disableHoverListener={destinationSupportsIban}
                              disableTouchListener={destinationSupportsIban}
                            >
                              <FormControlLabel
                                value={AccountIdTypeEnum.iban}
                                control={
                                  <Radio
                                    sx={{
                                      '& .MuiSvgIcon-root': {
                                        color: '#000',
                                      },
                                    }}
                                  />
                                }
                                label={t('iban', 'IBAN')}
                                disabled={
                                  accDetailsCheck.isIbanDisabled || !destinationSupportsIban
                                }
                              />
                            </Tooltip>
                            <Divider
                              orientation="vertical"
                              flexItem
                              sx={(theme) => ({
                                marginRight: theme.spacing(2),
                                [theme.breakpoints.down(390)]: {
                                  display: 'none',
                                },
                              })}
                            />
                            <FormControlLabel
                              value={AccountIdTypeEnum.accountNumber}
                              control={
                                <Radio
                                  sx={{
                                    '& .MuiSvgIcon-root': {
                                      color: '#000',
                                    },
                                  }}
                                />
                              }
                              label={t('accountNumber', 'Account number')}
                              disabled={accDetailsCheck.isIbanDisabled}
                            />
                          </RadioGroup>
                        )}
                      />
                    </Box>
                    {!!errors.accountIdType && (
                      <FormHelperText>{errors.accountIdType.message}</FormHelperText>
                    )}
                  </FormControl>
                </Box>

                <div>
                  <Typography variant="h6">{t('bank', 'Bank')}</Typography>
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 8 }}>
                      {accountIdType === AccountIdTypeEnum.accountNumber ? (
                        <StyledFormControl>
                          <FormControlledTextField
                            label={t('accountNumber', 'Account number')}
                            name="accountNumber"
                            type="text"
                            fullWidth
                            required={false}
                            onPaste={onPaste}
                          />
                        </StyledFormControl>
                      ) : (
                        <StyledFormControl>
                          <FormControlledTextField
                            label={t('iban', 'IBAN')}
                            name="iban"
                            type="text"
                            fullWidth
                            onInputChange={(e) => debouncedGetBankInfo(e.target.value)}
                            required={false}
                            onPaste={onPaste}
                            InputProps={
                              !isEmpty(iban) && !errors?.iban
                                ? {
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <Checked />
                                      </InputAdornment>
                                    ),
                                  }
                                : null
                            }
                          />
                        </StyledFormControl>
                      )}
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <StyledFormControl>
                        <FormControlledTextField
                          label={t('swiftBic', 'SWIFT / BIC').toUpperCase()}
                          name="swift"
                          type="text"
                          fullWidth
                          required={false}
                          sx={{
                            '& input': {
                              paddingRight: 0,
                            },
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end" sx={{ '& svg': { color: '#999' } }}>
                                <ControlledIconTooltip
                                  Icon={HelpOutlineIcon}
                                  placement="top"
                                  title={
                                    <Box sx={{ fontSize: '14px', lineHeight: '24px' }}>
                                      {t(
                                        'BicIsNotAlwaysRequired',
                                        'The BIC is not always required, but may be necessary for certain payment types (e.g. SWIFT). Providing it now is recommended if available.',
                                      )}
                                    </Box>
                                  }
                                />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </StyledFormControl>
                    </Grid>
                  </Grid>
                </div>

                <SpecificRequiredFields
                  country={
                    (destinationBankCountry ||
                      countryForSpecificRequiredFields) as keyof typeof countries
                  }
                  accountIdType={accountIdType}
                />

                <StyledFormControl
                  sx={(theme) => ({
                    '& .MuiFormHelperText-root': {
                      [theme.breakpoints.down('xs')]: {
                        position: 'relative',
                      },
                    },
                  })}
                >
                  <FormControlledTextField
                    label={t('bankName', 'Bank name')}
                    name="bankName"
                    type="text"
                    fullWidth
                    required={false}
                  />
                </StyledFormControl>

                <div>
                  <Typography variant="h6">{t('beneficiary', 'Beneficiary')}</Typography>
                  <StyledFormControl>
                    <FormControlledTextField
                      label={t('name', 'Name')}
                      name="name"
                      type="text"
                      fullWidth
                      required={false}
                      disabled={!!recipient}
                    />
                  </StyledFormControl>
                </div>

                <FormControl
                  sx={{
                    '& .MuiAutocomplete-root': {
                      margin: 0,
                    },
                    '& .MuiFormControl-marginNormal': {
                      margin: 0,
                    },
                  }}
                >
                  <CountryAutocompleteSelect
                    key={country}
                    name="country"
                    label={t('country', 'Country')}
                    clearFields={clearFields}
                    required={false}
                    excludeForbidden
                  />
                </FormControl>

                <FormControl>
                  <AddressAutocompleteSelect
                    name="streetAddress"
                    label={t('streetAddress', 'Street Address')}
                    handleChange={autocompleteFields}
                    countryCode={country}
                  />
                </FormControl>

                <StyledFormControl>
                  <FormControlledTextField
                    label={t('additionalDetailsOptional')}
                    name="additionalDetailsOptional"
                    type="text"
                    fullWidth
                    required={false}
                  />
                </StyledFormControl>

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 8 }}>
                    <StyledFormControl>
                      <Controller
                        name="city"
                        control={control}
                        render={(field) => (
                          <TextField
                            {...field}
                            sx={{
                              '& > div.MuiInputBase-root': {
                                marginTop: '5px',
                              },
                            }}
                            variant="standard"
                            label={t('city', 'City')}
                            error={!!errors?.city}
                            helperText={errors?.city?.message}
                          />
                        )}
                      />
                    </StyledFormControl>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 4 }}>
                    <StyledFormControl>
                      <Controller
                        name="postalCode"
                        control={control}
                        render={(field) => (
                          <TextField
                            {...field}
                            sx={{
                              '& > div.MuiInputBase-root': {
                                marginTop: '5px',
                              },
                            }}
                            variant="standard"
                            label={t('postalCode', 'Postal code')}
                            error={!!errors?.postalCode}
                            helperText={errors?.postalCode?.message}
                          />
                        )}
                      />
                    </StyledFormControl>
                  </Grid>
                </Grid>

                <StyledFormControl>
                  <FormControlledTextField
                    label={t(
                      'predefinedPaymentReferenceDescriptionOptional',
                      'Predefined Payment Reference/Description (optional)',
                    )}
                    name="commentTemplate"
                    type="text"
                    fullWidth
                    required={false}
                  />
                </StyledFormControl>

                <StyledFormControl>
                  <FormControlledTextField
                    label={t('aliasOptional', 'Alias (optional)')}
                    name="alias"
                    type="text"
                    fullWidth
                    required={false}
                  />
                </StyledFormControl>
              </DialogContent>

              <DialogActions>
                <Box
                  sx={(theme) => ({
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'flex-end',
                    paddingTop: theme.spacing(3),
                    gap: theme.spacing(3),
                    [theme.breakpoints.down('xs')]: {
                      padding: theme.spacing(3),
                      flexDirection: 'column',
                    },
                  })}
                >
                  <Button
                    variant="outlined"
                    disableElevation
                    onClick={handleModalClose}
                    fullWidth={isSmallScreen}
                  >
                    {t('cancel', 'Cancel')}
                  </Button>
                  <LoadingButton
                    type="submit"
                    variant="contained"
                    color="primary"
                    disableElevation
                    fullWidth={isSmallScreen}
                    loading={addRecipientLoading || editRecipientLoading}
                    disabled={isEmpty(formState.dirtyFields) || !isEmpty(errors)}
                  >
                    {recipient ? t('update', 'Update') : t('create', 'Create')}
                  </LoadingButton>
                </Box>
              </DialogActions>
            </form>
          </FormProvider>
        </Box>
      </Dialog>

      <CustomPrompt
        open={showWarning}
        onCancel={() => setShowWarning(false)}
        onConfirm={handleClose}
      />
    </>
  )
}
